import { tracer, useSendError } from "../observability/logging";
import {
  defaultTextMapSetter,
  ROOT_CONTEXT,
  Span,
  trace,
} from "@opentelemetry/api";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import settings from "../../constants/settings";
import { useCallback } from "react";
import { useAppSelector } from "./store";
import { useRouter } from "next/router";
import { getAccessToken } from "../accessToken";

export type FetchAndTraceHandler = (response: Response) => Promise<void> | void;

export type FetchAndTraceErrorHandler = () => void;

interface FetchAndTraceOptions {
  body?: BodyInit | null;
  method?: string;
  // Use null to specify that the Content-Type header should be omitted.
  contentType?: string | null;
}

export default function useFetchAndTrace(withSwr?: boolean) {
  const router = useRouter();
  const sendError = useSendError();
  const { signedIn, userId } = useAppSelector((state) => state.auth);

  const defaultFetcher = useCallback(
    async (
      callFetch: () => Promise<Response>,
      handler: FetchAndTraceHandler | null,
      errorHandler: FetchAndTraceErrorHandler | null,
      span: Span,
    ) => {
      try {
        const response = await callFetch();
        if (handler !== null) {
          return await handler(response);
        }
      } catch (error) {
        sendError(error, { activeSpan: span });
        if (errorHandler !== null) {
          errorHandler();
        }
      } finally {
        span.end();
      }
    },
    [sendError],
  );

  const swrFetcher = useCallback(
    async (callFetch: () => Promise<Response>, span: Span) => {
      try {
        return await callFetch();
      } finally {
        span.end();
      }
    },
    [],
  );

  return useCallback(
    async (
      url: string,
      handler: FetchAndTraceHandler | null,
      errorHandler: FetchAndTraceErrorHandler | null,
      { body, method, contentType }: FetchAndTraceOptions = {},
      noAuth = false,
      forceAuth = false,
    ) => {
      return tracer.startActiveSpan("fetchAndTrace", async (span: Span) => {
        span.setAttributes({
          url,
          method: method || "GET",
          gitCommitSha: settings.gitCommitSha,
          userId: userId || undefined,
          pageUrl: window.location.href,
        });

        const propagator = new W3CTraceContextPropagator();
        const carrier = {} as { [name: string]: string };

        propagator.inject(
          trace.setSpanContext(ROOT_CONTEXT, span.spanContext()),
          carrier,
          defaultTextMapSetter,
        );

        const headers: { [name: string]: string } = {
          ...carrier,
        };
        if (contentType === undefined) {
          headers["Content-Type"] = "application/json";
        }
        if ((signedIn && !noAuth) || forceAuth) {
          const cognitoAccessToken = await getAccessToken();
          if (cognitoAccessToken === undefined) {
            // In this case the auth state may be outdated so we want to reload the page
            return router.reload();
          }
          const jwtToken = cognitoAccessToken.toString();
          headers.Authorization = `Bearer ${jwtToken}`;
        }
        const callFetch = async () => {
          return await fetch(url, {
            method: method,
            body: body,
            headers: headers,
          });
        };

        if (withSwr) {
          return await swrFetcher(callFetch, span);
        } else {
          await defaultFetcher(callFetch, handler, errorHandler, span);
        }
      });
    },
    [userId, signedIn, withSwr, router, swrFetcher, defaultFetcher],
  );
}
